import { Injectable, computed, signal, inject, Injector, effect } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { getAuth, signOut } from 'firebase/auth';
import {
  FirebaseTokenData,
  PublicUser,
  User,
  UserSession,
  UserSessionResponse
} from '../../shared/types/user.types';
import { AmplitudeService } from './amplitude.service';
import { UserSessionService } from './user-session.service';
import { SupportedLanguages } from '../../shared/constants/translation-config';
import { ErrorModalService } from './error-modal.service';
import { FirebaseInitService } from './firebase-init.service';
import { AppRoutes, ROUTE_PATHS } from '../../shared/constants/app-routes';
import { InvitationEvent, UserLogOut } from '../../analytics/invitation-events';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // ===== ESTADO DE AUTENTICACIÓN =====
  public currentSession = signal<UserSession | null>(null);
  public currentUser = computed(() => this.currentSession()?.user || null);
  public isAuthenticated = computed(() => this.currentSession()?.isAuthenticated || false);

  private injector = inject(Injector);
  private _amplitudeService: AmplitudeService | null = null;
  private errorModalService = inject(ErrorModalService);
  private router = inject(Router);
  private firebaseInitService = inject(FirebaseInitService);
  
  constructor(
    private userSessionService: UserSessionService
  ) {
    this.initializeAuth();
    effect(() => {
      const user = this.currentUser();
      this.amplitudeService.updateCurrentUser(user);
    });
  }

  // Obtener AmplitudeService de forma lazy para actualizar el usuario
  private get amplitudeService(): AmplitudeService {
    if (!this._amplitudeService) {
      this._amplitudeService = this.injector.get(AmplitudeService);
    }
    return this._amplitudeService;
  }

  // Inicializa la autenticación al cargar la app
  private initializeAuth(): void {
    this.checkForStoredSession();
    if (!this.currentSession()) {
      this.createPublicSession();
    }
  }

  // Obtiene un valor del localStorage de forma segura
  private safeLocalStorageGet(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  // Guarda un valor en el localStorage de forma segura
  private safeLocalStorageSet(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Elimina un valor del localStorage de forma segura
  private safeLocalStorageRemove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Ignorar errores al eliminar el localStorage
    }
  }

  // Carga la sesión guardada desde localStorage
  private checkForStoredSession(): void {
    const storedSession = this.safeLocalStorageGet('userSession');
    if (storedSession) {
      try {
        const session: UserSession = JSON.parse(storedSession);
        this.currentSession.set(session);
      } catch (error) {
        this.createPublicSession();
      }
    }
  }

  // Crea una sesión pública para usuarios no autenticados
  private createPublicSession(): void {
    const publicUser: PublicUser = {
      sessionId: this.generateSessionId(),
      accessTime: new Date()
    };
    const session: UserSession = {
      user: null,
      i18n: 'es',
      isAuthenticated: false,
      sessionData: publicUser
    };
    this.currentSession.set(session);
    this.saveSession();
  }

  // Establece un usuario autenticado
  setAuthenticatedUser(userData: User, userSessionResponse?: UserSessionResponse): void {
    const userLocale = userSessionResponse?.user?.i18n.locale || 'es';

    const session: UserSession = {
      user: userData,
      i18n: userLocale,
      isAuthenticated: true
    };

    this.currentSession.set(session);
    this.saveSession();
  }

  // Establece un usuario autenticado con información completa del user-session
  setAuthenticatedUserWithSession(userData: User, userSessionResponse?: UserSessionResponse): void {
    // Si tenemos la respuesta del user-session, usamos los datos del backend
    if (userSessionResponse && userSessionResponse.user) {
      // Reemplazar el id del token por el id real de la BD
      userData.id = userSessionResponse.user.id;

      // También actualizar name y email si vienen del backend
      if (userSessionResponse.user.name) {
        userData.name = userSessionResponse.user.name;
      }
      if (userSessionResponse.user.email) {
        userData.email = userSessionResponse.user.email;
      }
      if (userSessionResponse.user.phoneDetail) {
        userData.phoneDetail = userSessionResponse.user.phoneDetail;
      }
      // Actualizar la foto de avatar si viene del backend
      if (userSessionResponse.user.biometric?.updatedFacial && 
          userSessionResponse.user.biometric.facial?.facialPhotoUrl) {
        userData.avatarPhotoUrl = userSessionResponse.user.biometric.facial.facialPhotoUrl;
      } else if (userSessionResponse.user.avatarPhotoUrl !== undefined) {
        userData.avatarPhotoUrl = userSessionResponse.user.avatarPhotoUrl;
      }
      // Actualizar el idioma
      if (userSessionResponse.user.i18n) { userData.i18n = userSessionResponse.user.i18n; }

      // Extraer buildingId y unitId de la primera unidad
      if (userSessionResponse.units && userSessionResponse.units.length > 0) {
        const firstUnit = userSessionResponse.units[0];
        userData.buildingId = firstUnit.building.id;
        userData.unitId = firstUnit.id;
        userData.unitNumber = firstUnit.number;
        userData.i18n = firstUnit.building.i18n;
      }
      if (userSessionResponse.user.termsAccepted !== undefined) {
        userData.termsAccepted = userSessionResponse.user.termsAccepted;
      }
    }

    const session: UserSession = {
      user: userData,
      i18n: userSessionResponse?.user?.i18n.locale || 'es',
      isAuthenticated: true
    };
    this.currentSession.set(session);
    this.saveSession();
  }

  // Autentica al usuario usando un token de Firebase (versión básica - sin user-session)
  authenticateWithFirebaseToken(firebaseTokenData: FirebaseTokenData): void {
    if (!firebaseTokenData.token) return;

    try {
      const userInfo = this.extractUserInfoFromToken(firebaseTokenData.token);
      const user: User = {
        id: userInfo.id,
        name: userInfo.name,
        email: userInfo.email,
        firebaseToken: firebaseTokenData.token
      };
      this.setAuthenticatedUser(user);
    } catch (error) {
      const fallbackUser: User = {
        id: 'user_' + Date.now(),
        name: 'Usuario',
        email: '',
        firebaseToken: firebaseTokenData.token
      };
      this.setAuthenticatedUser(fallbackUser);
    }
  }

  // Autentica al usuario usando un token de Firebase y obtiene información completa del user-session
  authenticateWithFirebaseTokenAndSession(firebaseTokenData: FirebaseTokenData): Observable<UserSessionResponse | null> {
    if (!firebaseTokenData.token) {
      return of(null);
    }

    // Primero extraemos la información básica del token
    let basicUser: User;
    try {
      const userInfo = this.extractUserInfoFromToken(firebaseTokenData.token);
      basicUser = {
        id: userInfo.id,
        name: userInfo.name,
        email: userInfo.email,
        firebaseToken: firebaseTokenData.token
      };
    } catch (error) {
      basicUser = {
        id: 'user_' + Date.now(),
        name: 'Usuario',
        email: '',
        firebaseToken: firebaseTokenData.token
      };
    }

    // Establecemos el usuario básico primero para que el interceptor pueda agregar el token automáticamente
    this.setAuthenticatedUser(basicUser);

    // Intentamos obtener la sesión completa del backend
    // El token se agrega automáticamente por el AuthInterceptor
    return this.userSessionService.getUserSession().pipe(
      map(response => {
        const userSessionResponse = response.data;
        this.setAuthenticatedUserWithSession(basicUser, userSessionResponse);
        return userSessionResponse;
      }),
      catchError(error => {
        this.clearLocalSession();
        if (!this.firebaseInitService.isFirebaseInitialized()) {
          this.firebaseInitService.init();
        }
        try {
          const auth = getAuth();
          if (auth.currentUser) {
            signOut(auth).catch(() => {});
          }
        } catch {
          // Ignorar si getAuth/signOut falla
        }
        return throwError(() => error);
      })
    );
  }

  // Extrae toda la información del usuario del token JWT
  private extractUserInfoFromToken(token: string): { id: string; name: string; email: string } {
    try {
      const payload = this.decodeTokenPayload(token);
      return {
        id: payload.sub || payload.user_id || payload.uid || 'unknown',
        name: payload.name || payload.display_name || payload.given_name || 'Usuario',
        email: payload.email || ''
      };
    } catch (error) {
      return {
        id: 'unknown',
        name: 'Usuario',
        email: ''
      };
    }
  }

  // Decodifica el payload del token JWT
  private decodeTokenPayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) {
        throw new Error('Token JWT inválido');
      }
      return JSON.parse(atob(parts[1]));
    } catch (error) {
      throw error;
    }
  }


  // Genera un ID único para la sesión
  private generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  // Guarda la sesión actual en localStorage
  private saveSession(): void {
    const session = this.currentSession();
    if (session) {
      this.safeLocalStorageSet('userSession', JSON.stringify(session));
    }
  }

  // Marca en la sesión local que el usuario aceptó términos y condiciones.
  markTermsAccepted(): void {
    const session = this.currentSession();
    if (session?.user) {
      const updatedUser = { ...session.user, termsAccepted: true };
      this.currentSession.set({ ...session, user: updatedUser });
      this.saveSession();
    }
  }

  // Cierra la sesión del usuario con Firebase
  logout(): void {
    // Enviar evento de User Log Out a Amplitude antes de limpiar la sesión
    const userLogOutEvent = new UserLogOut();
    this.amplitudeService.logEvent(InvitationEvent.userLogOut, userLogOutEvent);
    
    // Asegurar que Firebase esté inicializado
    if (!this.firebaseInitService.isFirebaseInitialized()) {
      this.firebaseInitService.init();
    }

    // Cerrar sesión en Firebase
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        signOut(auth)
          .then(() => {
            // Limpiar sesión local
            this.clearLocalSession();
            // Redirigir a la vista de autenticación
            this.router.navigate([ROUTE_PATHS[AppRoutes.LOGIN]]);
          })
          .catch((error) => {
            console.error('Error al cerrar sesión en Firebase:', error);
            // Aún así limpiar la sesión local y redirigir
            this.clearLocalSession();
            this.router.navigate([ROUTE_PATHS[AppRoutes.LOGIN]]);
          });
      } else {
        // Si no hay usuario en Firebase, solo limpiar sesión local
        this.clearLocalSession();
        this.router.navigate([ROUTE_PATHS[AppRoutes.LOGIN]]);
      }
    } catch (error) {
      console.error('Error al obtener instancia de Firebase Auth:', error);
      // Aún así limpiar la sesión local y redirigir
      this.clearLocalSession();
      this.router.navigate([ROUTE_PATHS[AppRoutes.LOGIN]]);
    }
  }

  // Limpia la sesión local del usuario
  private clearLocalSession(): void {
    this.currentSession.set(null);
    this.safeLocalStorageRemove('userSession');
  }

  // Actualiza la información del user-session para el usuario actualmente autenticado
  updateUserSessionInfo(): Observable<UserSessionResponse | null> {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return of(null);
    }

    return this.userSessionService.getUserSession().pipe(
      map(response => {
        const userSessionResponse = response.data;
        if (userSessionResponse && userSessionResponse.user) {
          // Actualizar con los datos del backend (más confiables)
          const updatedUser: User = {
            ...currentUser,
            // Reemplazar el id del token por el id real de la BD
            id: userSessionResponse.user.id,
            // Actualizar name y email si vienen del backend
            name: userSessionResponse.user.name || currentUser.name,
            email: userSessionResponse.user.email || currentUser.email,
          };

          if (userSessionResponse.user.phoneDetail) {
            updatedUser.phoneDetail = userSessionResponse.user.phoneDetail;
          }

          // Actualizar la foto de avatar si viene del backend
          // Priorizar foto biométrica si está disponible y actualizada
          if (userSessionResponse.user.biometric?.updatedFacial && 
              userSessionResponse.user.biometric.facial?.facialPhotoUrl) {
            updatedUser.avatarPhotoUrl = userSessionResponse.user.biometric.facial.facialPhotoUrl;
          } else if (userSessionResponse.user.avatarPhotoUrl !== undefined) {
            updatedUser.avatarPhotoUrl = userSessionResponse.user.avatarPhotoUrl;
          }

          // Extraer buildingId y unitId de la primera unidad
          if (userSessionResponse.units && userSessionResponse.units.length > 0) {
            const firstUnit = userSessionResponse.units[0];
            updatedUser.buildingId = firstUnit.building.id;
            updatedUser.unitId = firstUnit.id;
            updatedUser.unitNumber = firstUnit.number;
          }

          if (userSessionResponse.user.termsAccepted !== undefined) {
            updatedUser.termsAccepted = userSessionResponse.user.termsAccepted;
          }

          const userLocale = this.getCurrentUserLanguageFromNavigator();
          const session: UserSession = {
            user: updatedUser,
            i18n: userLocale,
            isAuthenticated: true
          };
          this.currentSession.set(session);
          this.saveSession();
        }
        return userSessionResponse;
      }),
      catchError(error => {
        const errorMessage = error?.error?.error?.message;
        this.errorModalService.openErrorModal(errorMessage);
        return of(null);
      })
    );
  }

  // Método helper para obtener buildingId y unitId del usuario actual
  getCurrentUserBuildingAndUnit(): { buildingId?: string; unitId?: string } {
    const currentUser = this.currentUser();
    return {
      buildingId: currentUser?.buildingId,
      unitId: currentUser?.unitId
    };
  }

  // Obtiene el idioma del usuario actual
  getCurrentUserLanguage(): string {
    return this.getCurrentUserLanguageFromNavigator();
  }

  // Verifica si el token nuevo es diferente al token actualmente guardado
  isNewToken(token: string): boolean {
    const currentUser = this.currentUser();
    return !currentUser || !currentUser.firebaseToken || currentUser.firebaseToken !== token;
  }

  // Verifica si Firebase tiene una sesión activa (usuario logueado en Firebase).
  async hasActiveFirebaseSession(): Promise<boolean> {
    if (!this.firebaseInitService.isFirebaseInitialized()) {
      this.firebaseInitService.init();
    }
    const auth = getAuth();
    return auth.currentUser !== null;
  }

  // Renueva el token de Firebase (getIdToken(true)) y actualiza la sesión local.
  async refreshFirebaseToken(): Promise<string | null> {
    if (!this.firebaseInitService.isFirebaseInitialized()) {
      this.firebaseInitService.init();
    }
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return null;
    }
    try {
      const token = await firebaseUser.getIdToken(true);
      const session = this.currentSession();
      if (session?.user) {
        const updatedUser = { ...session.user, firebaseToken: token };
        const newSession = { ...session, user: updatedUser };
        this.currentSession.set(newSession);
        this.saveSession();
      }
      return token;
    } catch (err) {
      console.error('Error refrescando token de Firebase:', err);
      return null;
    }
  }

  private getCurrentUserLanguageFromNavigator(): string {
    return navigator?.language?.toLocaleLowerCase().includes(SupportedLanguages.ES) ? SupportedLanguages.ES : SupportedLanguages.EN;
  }
}

